<template>
    <div class="origin-destination mb-3" :id="`OriginDestination${originDestinationIndex}`">
        <accent-box>
            <template v-slot:title>
                <div class="origin-destination-title row align-items-center">
                    <h4 class="col-auto text-dark mb-2">
                        <font-awesome-icon :icon="['fal', originDestination.type === 'inbound' ? 'plane-arrival' : 'plane-departure']" class="fa-lg me-2"/>
                        {{$t(`trip.originDestination.type.${originDestination.type}`, {number: originDestinationIndex+1})}}
                    </h4>
                    <h4 class="col departure-date flex-grow-1 mb-2">
                        {{$filters.beautifyDate(originDestination.departureDateTime)}}
                    </h4>
                    <div class="col-lg-auto lead mb-2">{{departureAirport.city}} &ndash; {{arrivalAirport.city}}</div>
                </div>

            </template>
            <div
                @click="toggleDetailed"
                class="origin-destination-summary row align-items-center"
            >
                <div class="col-6 col-lg-auto order-1 mb-3">
                    <div v-if="airlines.length === 1" class="d-flex flex-column align-items-center">
                        <img :src="airlines[0].logoUrl" :alt="airlines[0].name" class="airline-logo mb-1"/>
                        <div class="origin-destination-airline-name">{{airlines[0].name}}</div>
                    </div>
                    <!--TODO use Font Awesome layers to show a visual indicator that there are multiple airlines. -->
                    <!--TODO make sure this looks good on mobile -->
                    <div v-else class="d-flex flex-column align-items-center">
                        <font-awesome-icon :icon="['fad', 'plane-engines']" class="fa-3x text-secondary mb-2" />
                        <strong class="origin-destination-airline-name">{{$t('common.itinerary.multipleAirlines')}}</strong>
                    </div>
                </div>
                <div class="col-lg order-3 order-lg-2 mb-3">
                    <div class="flight-times row align-items-center justify-content-center">
                        <div class="col-auto col-lg text-end">
                            <div class="lead">
                                <strong>{{$filters.beautifyTime(originDestination.departureDateTime)}}</strong>
                            </div>
                            <div class="lead">{{originDestination.departureAirportCode}}</div>
                            <div>{{departureAirport.city}}</div>
                        </div>
                        <div class="col col-lg-3">
                            <div class="d-flex justify-content-evenly">
                                <origin-destination-line-pin
                                    v-for="flight in originDestination.flights"
                                    :key="flight.id"

                                    :flight="flight"
                                    :origin-destination="originDestination"
                                    :trip="trip"
                                />
                            </div>
                            <hr class="origin-destination-line mt-0 mb-2"/>
                            <div class="text-center">{{$tc('trip.originDestination.stops', numberOfStops)}}</div>
                        </div>
                        <div class="col-auto col-lg">
                            <div class="lead">
                                <strong>{{$filters.beautifyTime(originDestination.arrivalDateTime)}}</strong>
                            </div>
                            <div class="lead">{{originDestination.arrivalAirportCode}}</div>
                            <div>{{arrivalAirport.city}}</div>
                        </div>
                    </div>
                </div>
                <div class="col-6 col-lg-auto order-2 order-lg-3 lead text-center mb-3">
                    <div class="duration mb-1"><strong>{{originDestination.duration}}</strong></div>
                    <div class="duration-label">{{$t('common.itinerary.duration')}}</div>
                </div>
                <!--Show operating airlines as required by regulation. -->
                <div v-if="operatingAirlineNames.length && !detailed" class="col-12 order-4 mb-3">
                    <small class="text-muted">{{$t('common.itinerary.operatedBy', {operatingAirlineNames: operatingAirlineNames.join(', ')})}}</small>
                </div>
            </div>

            <div v-show="detailed" class="mt-3">
                <div
                    v-for="flight in originDestination.flights"
                    :key="flight.id"
                >
                    <flight
                        :flight="flight"
                        :trip="trip"
                        :origin-destination="originDestination"
                    />
                </div>
            </div>
        </accent-box>

    </div>
</template>

<script>
//P4MT Components
import AccentBox from '@/components/common/AccentBox';
import Flight from '@/components/itinerary/Flight';
import OriginDestinationLinePin from '@/components/itinerary/OriginDestinationLinePin';


export default {
    name: 'OriginDestination',
    components: {
        AccentBox,
        Flight,
        OriginDestinationLinePin
    },
    props: {
        trip: {
            type: Object,
            required: true
        },
        originDestination: {
            type: Object,
            required: true
        },
        originDestinationIndex: {
            type: Number,
            required: true
        },
        detailed: {
            type: Boolean,
            required: true
        }
    },
    emits: ['toggleDetailed'],
    computed: {
        airlines() {
            let airlines = [];
            this.originDestination.flights.forEach(flight => {
                //Find & add the airline if it is not already present in the airlines array.
                if(airlines.some(airline => airline.iata === flight.airlineCode) === false) {
                    airlines.push(Object.values(this.trip.pnr.airlines).find(airline => airline.iata === flight.airlineCode));
                }
            });
            return airlines;
        },
        departureAirport() {
            return Object.values(this.trip.pnr.airports).find(airport => airport.iata === this.originDestination.departureAirportCode);
        },
        arrivalAirport() {
            return Object.values(this.trip.pnr.airports).find(airport => airport.iata === this.originDestination.arrivalAirportCode);
        },

        //Aggregate code share info from each flight in order to provide the required code share synopsis in the OD summary.
        operatingAirlineNames() {
            let operatingAirlineNames = [];
            this.originDestination.flights.forEach(flight => {
                if(flight.prettyOperatingAirlineName && !operatingAirlineNames.includes(flight.prettyOperatingAirlineName)) {
                    operatingAirlineNames.push(flight.prettyOperatingAirlineName);
                }
            });
            return operatingAirlineNames;
        },

        //TODO remove me and use this.originDestination.numberOfStops, once Tim gets it populated properly on every trip retrieval.
        numberOfStops() {
            let numberOfStops = -1;
            this.originDestination.flights.forEach(flight => {
                numberOfStops += flight.numberOfStops + 1;
            });
            return numberOfStops;
        }
    },
    methods: {
        toggleDetailed() {
            //Notify parent component to toggle the value of the "detailed" prop.
            this.$emit('toggleDetailed');

            //Wait for next rendering cycle after emitting.
            this.$nextTick(() => {
                //Scroll to the origin destination that was just clicked, as it may be further down the page now, especially on mobile
                document.getElementById(`OriginDestination${this.originDestinationIndex}`).scrollIntoView({behavior: 'smooth', block: 'start'});
            });

        }
    }
};
</script>

<style lang="scss" scoped>
.origin-destination {
    .departure-date {
        font-weight: 300;
    }

    .airline-logo {
        max-width: 70px;
    }

    .origin-destination-summary {
        cursor: pointer;
    }
    .origin-destination-line {
        background-color: $dark;
        opacity: 1.0;
    }

    //Custom styling for accent box child component.
    //:deep allows properly scoped styling of the child component.
    &:deep(.accent-box) {
        .accent-box-body {
            padding-bottom: 0;
        }
    }
}

@include media-breakpoint-down(sm) {

    .origin-destination {
        .origin-destination-title {
            h4 {
                font-size: 1.25rem;
            }
            .lead {
                font-size: 1.0rem;
            }
        }
        .airline-logo {
            max-width: 35px;
        }
        .duration {
            line-height: 35px;
        }
        .duration-label {
            font-size: 1.0rem;
        }
        .flight-times {
            font-size: 0.875rem;
            .lead {
                font-size: 1.0rem;
            }
        }

        //Custom styling for accent box child component.
        //:deep allows properly scoped styling of the child component.
        &:deep(.accent-box) {
            .accent-box-body {
                padding-left: 0.5rem;
                padding-right: 0.5rem;
            }
        }
    }

}
</style>